"use client";
import { motion } from "framer-motion";
import { useState } from "react";
import { cn } from "./cn";

export const GlowingCard = ({ children, className, glowColor = "cyan", icon, title }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const glowColors = {
    cyan: {
      border: "from-cyan-400 via-cyan-500 to-cyan-400",
      bg: "from-cyan-500/15 via-cyan-400/5 to-transparent",
      accent: "cyan-400",
      shadow: "shadow-cyan-500/15",
    },
    silver: {
      border: "from-gray-300 via-white to-gray-300",
      bg: "from-white/10 via-gray-400/5 to-transparent",
      accent: "gray-300",
      shadow: "shadow-white/15",
    },
    turquoise: {
      border: "from-teal-400 via-cyan-400 to-teal-400",
      bg: "from-teal-500/15 via-cyan-400/5 to-transparent",
      accent: "teal-400",
      shadow: "shadow-teal-500/15",
    },
    white: {
      border: "from-white via-gray-200 to-white",
      bg: "from-white/10 via-gray-300/5 to-transparent",
      accent: "white",
      shadow: "shadow-white/20",
    },
  };

  const colors = glowColors[glowColor] || glowColors.cyan;

  return (
    <motion.div 
      className={cn("relative group", className)}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      {/* Animated border glow */}
      <motion.div
        className={cn(
          "absolute -inset-[1px] rounded-3xl bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-500",
          colors.border
        )}
        animate={{
          backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "linear",
        }}
        style={{ backgroundSize: "200% 200%" }}
      />
      
      {/* Outer glow - reduced size */}
      <div
        className={cn(
          "absolute -inset-2 rounded-3xl bg-gradient-to-r opacity-0 blur-md transition-opacity duration-500 group-hover:opacity-25",
          colors.border
        )}
      />
      
      {/* Card content */}
      <div className={cn(
        "relative rounded-3xl overflow-hidden",
        "bg-gradient-to-br from-gray-900/95 via-gray-800/90 to-gray-900/95",
        "backdrop-blur-xl",
        "border border-white/10 group-hover:border-white/20",
        "transition-all duration-500",
        `hover:${colors.shadow} hover:shadow-2xl`
      )}>
        {/* Inner gradient overlay */}
        <div className={cn(
          "absolute inset-0 bg-gradient-to-br opacity-50",
          colors.bg
        )} />
        
        {/* Spotlight effect following mouse */}
        {isHovered && (
          <motion.div
            className="absolute inset-0 opacity-50 pointer-events-none"
            style={{
              background: `radial-gradient(400px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(255,255,255,0.1), transparent 40%)`,
            }}
          />
        )}
        
        {/* Corner accent */}
        <div className={cn(
          "absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl opacity-20",
          colors.bg
        )} />
        
        {/* Content wrapper */}
        <div className="relative p-8">
          {/* Optional icon */}
          {icon && (
            <div className={cn(
              "w-12 h-12 rounded-2xl mb-4 flex items-center justify-center",
              "bg-gradient-to-br",
              colors.bg,
              "border border-white/10"
            )}>
              {icon}
            </div>
          )}
          
          {/* Optional title */}
          {title && (
            <h3 className={cn(
              "text-xl font-bold mb-4",
              `text-${colors.accent}`
            )}>
              {title}
            </h3>
          )}
          
          {children}
        </div>
        
        {/* Bottom accent line */}
        <div className={cn(
          "absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-500",
          colors.border
        )} />
      </div>
    </motion.div>
  );
};

export default GlowingCard;
